import {
  type EvidenceUploadMode,
  useApiInspectionsV1EvidenceBulkDeleteCreate,
  useApiLogisticsV1LoadsEvidenceBulkDeleteCreate,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceBulkDeleteCreate,
  useApiQualityExternalUploadV1EvidenceBulkDeleteCreate,
  useApiQualitySessionsV2TradeCompanySessionEvidenceBulkDeleteCreate,
  useApiSamplesV1SamplesEvidenceBulkDeleteCreate,
  useApiTradeV1SiteMaterialsEvidenceBulkDeleteCreate,
} from 'api';
import { logger } from 'monitor';
import { useCallback, useMemo } from 'react';

type BulkDeletePayload = {
  data: {
    evidence_ids: number[];
  };
};

type UseBulkDeleteEvidenceArgs = {
  mode?: EvidenceUploadMode;
  onError?: (error: unknown) => void;
  onSuccess?: (evidenceIds: number[]) => void;
  parentId?: string | number;
};

export const useBulkDeleteEvidence = ({ mode, onError, onSuccess, parentId }: UseBulkDeleteEvidenceArgs) => {
  const { mutateAsync: deleteInspectionEvidence, isPending: isInspectionDeletePending } =
    useApiInspectionsV1EvidenceBulkDeleteCreate();
  const { mutateAsync: deleteLoadEvidence, isPending: isLoadDeletePending } =
    useApiLogisticsV1LoadsEvidenceBulkDeleteCreate();
  const { mutateAsync: deleteExternalLoadEvidence, isPending: isExternalLoadDeletePending } =
    useApiQualityExternalLoadsV1ExternalLoadsEvidenceBulkDeleteCreate();
  const { mutateAsync: deleteExternalEvidence, isPending: isExternalEvidenceDeletePending } =
    useApiQualityExternalUploadV1EvidenceBulkDeleteCreate();
  const { mutateAsync: deleteTradeCompanySessionEvidence, isPending: isTradeCompanySessionDeletePending } =
    useApiQualitySessionsV2TradeCompanySessionEvidenceBulkDeleteCreate();
  const { mutateAsync: deleteSampleEvidence, isPending: isSampleDeletePending } =
    useApiSamplesV1SamplesEvidenceBulkDeleteCreate();
  const { mutateAsync: deleteSiteMaterialEvidence, isPending: isSiteMaterialDeletePending } =
    useApiTradeV1SiteMaterialsEvidenceBulkDeleteCreate();

  const isPending = useMemo(() => {
    switch (mode) {
      case 'inspector':
        return isInspectionDeletePending;
      case 'load':
        return isLoadDeletePending;
      case 'sample':
        return isSampleDeletePending;
      case 'site-material':
        return isSiteMaterialDeletePending;
      case 'trade-company-session':
        return isTradeCompanySessionDeletePending;
      case 'external-evidence':
        return isExternalEvidenceDeletePending;
      case 'external-load':
        return isExternalLoadDeletePending;
      default:
        return false;
    }
  }, [
    isExternalEvidenceDeletePending,
    isExternalLoadDeletePending,
    isInspectionDeletePending,
    isLoadDeletePending,
    isSampleDeletePending,
    isSiteMaterialDeletePending,
    isTradeCompanySessionDeletePending,
    mode,
  ]);

  const deleteEvidenceAsync = useCallback(
    async (evidenceIds: number[]): Promise<boolean> => {
      if (!mode || parentId === undefined || parentId === null) {
        logger.warn('Bulk delete requires a mode and parentId.', { evidenceIds, mode, parentId });
        return false;
      }

      try {
        switch (mode) {
          case 'inspector':
            await deleteInspectionEvidence({
              data: { evidence_ids: evidenceIds },
              slug: String(parentId),
            });
            break;
          case 'load':
            await deleteLoadEvidence({
              data: { evidence_ids: evidenceIds },
              loadId: String(parentId),
            });
            break;
          case 'sample':
            await deleteSampleEvidence({
              data: { evidence_ids: evidenceIds },
              objectId: Number(parentId),
            });
            break;
          case 'site-material':
            await deleteSiteMaterialEvidence({
              data: { evidence_ids: evidenceIds },
              siteMaterialId: String(parentId),
            });
            break;
          case 'trade-company-session':
            await deleteTradeCompanySessionEvidence({
              data: { evidence_ids: evidenceIds },
              tradeCompanySessionId: Number(parentId),
            });
            break;
          case 'external-evidence':
            await deleteExternalEvidence({
              data: { evidence_ids: evidenceIds },
              uuid: String(parentId),
            });
            break;
          case 'external-load':
            await deleteExternalLoadEvidence({
              data: { evidence_ids: evidenceIds },
              externalLoadId: Number(parentId),
            });
            break;
          default:
            logger.warn('Unsupported bulk delete mode.', { evidenceIds, mode, parentId });
            return false;
        }

        onSuccess?.(evidenceIds);
        return true;
      } catch (error) {
        onError?.(error);
        return false;
      }
    },
    [
      deleteExternalEvidence,
      deleteExternalLoadEvidence,
      deleteInspectionEvidence,
      deleteLoadEvidence,
      deleteSampleEvidence,
      deleteSiteMaterialEvidence,
      deleteTradeCompanySessionEvidence,
      mode,
      onError,
      onSuccess,
      parentId,
    ]
  );

  const deleteEvidence = useCallback(
    ({ data }: BulkDeletePayload) => {
      void deleteEvidenceAsync(data.evidence_ids);
    },
    [deleteEvidenceAsync]
  );

  return { deleteEvidence, deleteEvidenceAsync, isPending };
};
